import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';

//components
import { Tooltip } from 'dibs-elements/exports/Tooltip';
import {
    anywhereInWorld,
    ratesVary,
    freeQuote,
    quotePending,
    quotePrice,
    quoteMethod,
    quoteMethodVariant,
    quoteRegion,
    quoteHandlingTime,
    leadTimeMessage,
    shippingMethodDescriptions,
} from '../messages/pdpShippingInfoMessages';

//helpers
import get from 'lodash.get';
import { getShippingPrice, REGION_CODES } from '../helpers/shippingHelpers';

//styles
import styles from './PdpSharedShippingInfo.scss';

export class PdpSharedShippingInfoComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            tooltipHover: false,
        };

        this.onHover = this.onHover.bind(this);
    }

    onHover(tooltipHover) {
        this.setState({ tooltipHover });
    }

    render() {
        const {
            currency,
            children,
            shipmentQuote,
            shipment,
            quoteRequestStatus,
            leadTime,
            renderTooltip,
            hidePrice,
            noLineBreaks,
            isFreeShipping,
            isPdpFreeShippingTestVariant,
        } = this.props;

        const { tooltipHover } = this.state;

        const relevantQuote = shipmentQuote || null;
        const customQuoteShippingAddress = get(shipment, 'shippingAddress.displayCityStateCountry');
        const convertedAmountList = get(relevantQuote, 'buyerTotalAmount.convertedAmountList');
        let regionName =
            quoteRequestStatus === 'customQuoteProvided' ||
            quoteRequestStatus === 'customQuoteRequested'
                ? customQuoteShippingAddress
                : get(relevantQuote, 'serviceMethod.region.regionName');
        const shippingMethodDisplay = get(relevantQuote, 'buyerServiceMethodDisplay');
        const regionCode = get(relevantQuote, 'serviceMethod.region.code');
        const price = getShippingPrice({ convertedAmountList, currency });
        const handlingTimeDisplay = get(relevantQuote, 'handlingTimeDisplay');
        let overviewMessage;

        let shippingPriceQuote;
        let shippingMethodQuote;
        if (isPdpFreeShippingTestVariant) {
            shippingPriceQuote = isFreeShipping ? freeQuote : quotePrice(price);
            shippingMethodQuote = quoteMethodVariant(shippingMethodDisplay);
        } else {
            shippingPriceQuote = !hidePrice && quotePrice(price);
            shippingMethodQuote = quoteMethod(shippingMethodDisplay);
        }

        if (regionCode === REGION_CODES.REST_OF_WORLD) {
            regionName = anywhereInWorld;
        }
        if (!relevantQuote || quoteRequestStatus === 'requestQuote') {
            overviewMessage = ratesVary;
        } else if (quoteRequestStatus === 'customQuoteRequested') {
            overviewMessage = (
                <span data-tn="quote-requested">{quotePending(customQuoteShippingAddress)}</span>
            );
        } else {
            overviewMessage = (
                <span data-tn="prequote-overview">
                    {shippingPriceQuote}
                    <div
                        className={styles.hoverable}
                        onMouseEnter={() => {
                            this.onHover(true);
                        }}
                        onMouseLeave={() => {
                            this.onHover(false);
                        }}
                    >
                        {shippingMethodQuote}
                        {renderTooltip && (
                            <Tooltip isVisible={tooltipHover} direction="bottom">
                                {shippingMethodDescriptions[shippingMethodDisplay]}
                            </Tooltip>
                        )}
                    </div>{' '}
                    {quoteRegion(regionName)}
                    {!noLineBreaks && <div />}
                    {quoteHandlingTime(handlingTimeDisplay)}
                    {leadTime !== 'available-now' && (
                        <span data-tn="lead-time">{leadTimeMessage}</span>
                    )}
                    {'. '}
                </span>
            );
        }

        return (
            <span className={styles.shippingInfoContainer}>
                {overviewMessage}
                {children}
            </span>
        );
    }
}

PdpSharedShippingInfoComponent.propTypes = {
    shipmentQuote: PropTypes.object,
    shipment: PropTypes.object,
    currency: PropTypes.string.isRequired,
    quoteRequestStatus: PropTypes.string,
    children: PropTypes.node,
    leadTime: PropTypes.string,
    renderTooltip: PropTypes.bool,
    hidePrice: PropTypes.bool,
    noLineBreaks: PropTypes.bool,
    isFreeShipping: PropTypes.bool,
    isPdpFreeShippingTestVariant: PropTypes.bool,
};

export const PdpSharedShippingInfo = createFragmentContainer(PdpSharedShippingInfoComponent, {
    shipment: graphql`
        fragment PdpSharedShippingInfo_shipment on Shipment {
            shippingAddress {
                displayCityStateCountry
            }
        }
    `,
    shipmentQuote: graphql`
        fragment PdpSharedShippingInfo_shipmentQuote on ShipmentQuote {
            handlingTimeDisplay(displayShort: true)
            totalAmount {
                currency
                amount
                convertedAmountList {
                    currency
                    amount
                }
            }
            buyerTotalAmount {
                convertedAmountList {
                    currency
                    amount
                }
            }
            buyerServiceMethodDisplay
            serviceMethod {
                serviceMethodType
                region {
                    apiType
                    code
                    regionName
                    regionType
                }
            }
        }
    `,
});
